// @systeme: auth-identite
// ============================================================================
// CECODEV Platform - Login Page (v2 - 2FA flow)
// ============================================================================

import { ArrowLeft, Eye, EyeOff } from "lucide-react";
import { useState, useRef, useEffect } from "react";
import { codeApi, messageApi } from "../lib/api";
import { Link, useSearchParams } from "react-router-dom";
import {
  Box,
  Card,
  CardContent,
  TextField,
  Button,
  Typography,
  Stack,
  CircularProgress,
  IconButton,
  InputAdornment,
  Alert,
} from "@mui/material";
import { useAuth } from "../contexts/AuthContext";
import { toast } from "sonner";

export default function LoginPage() {
  const [searchParams] = useSearchParams();
  const [email, setEmail] = useState(() => searchParams.get("email") ?? "");
  const [password, setPassword] = useState("");
  const [code, setCode] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [noPasswordError, setNoPasswordError] = useState(false);
  const [resendCooldown, setResendCooldown] = useState(0);
  const { login, verify2FA, resendCode, resetLoginFlow, loginStep, pendingEmailMasked } = useAuth();
  const codeInputRef = useRef<HTMLInputElement>(null);

  // Focus le champ code quand on passe en 2FA
  useEffect(() => {
    if (loginStep === "2FA_REQUIRED") {
      setTimeout(() => codeInputRef.current?.focus(), 100);
    }
  }, [loginStep]);

  // Cooldown pour le renvoi de code
  useEffect(() => {
    if (resendCooldown > 0) {
      const timer = setTimeout(() => setResendCooldown(resendCooldown - 1), 1000);
      return () => clearTimeout(timer);
    }
  }, [resendCooldown]);

  async function handleLogin(e: React.FormEvent) {
    e.preventDefault();
    setIsLoading(true);
    setNoPasswordError(false);
    try {
      await login(email, password);
    } catch (error) {
      if (codeApi(error) === "NO_PASSWORD") {
        setNoPasswordError(true);
      } else {
        toast.error(messageApi(error, "Erreur de connexion"));
      }
    } finally {
      setIsLoading(false);
    }
  }

  async function handleVerify2FA(e: React.FormEvent) {
    e.preventDefault();
    if (code.length !== 6) return;
    setIsLoading(true);
    try {
      await verify2FA(code);
      toast.success("Connexion réussie");
    } catch (error) {
      toast.error(messageApi(error, "Code invalide"));
      setCode("");
    } finally {
      setIsLoading(false);
    }
  }

  async function handleResend() {
    try {
      await resendCode();
      toast.success("Nouveau code envoyé");
      setResendCooldown(60);
    } catch (error) {
      toast.error("Erreur lors du renvoi du code");
    }
  }

  function handleBack() {
    resetLoginFlow();
    setCode("");
  }

  // Filtrer l'input du code : uniquement des chiffres, max 6
  function handleCodeChange(value: string) {
    const digits = value.replace(/\D/g, "").slice(0, 6);
    setCode(digits);
  }

  return (
    <Box className="auth-background">
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          minHeight: "100vh",
          px: 2,
        }}
      >
        <Card
          sx={{
            width: "100%",
            maxWidth: 480,
            boxShadow: "0 20px 60px rgba(0, 0, 0, 0.15)",
          }}
          className="fade-in"
        >
          <CardContent sx={{ p: 5 }}>
            {/* Logo Pairsform (FX 2026-08-18) — il porte déjà la signature
                « L'intelligence collective facilitée, par Cecodev », donc plus
                de titre ni de baseline en doublon sous l'image. */}
            <Box sx={{ textAlign: "center", mb: 4 }}>
              <Box
                component="img"
                src="/logo-pairsform-cecodev-v2.png"
                alt="Pairsform — l'intelligence collective facilitée, par Cecodev"
                sx={{ width: "100%", maxWidth: 320, height: "auto", mx: "auto", display: "block" }}
              />
            </Box>

            {/* ── Étape 1 : Email + Mot de passe ── */}
            {loginStep === "IDLE" && (
              <form onSubmit={handleLogin}>
                <Stack spacing={3}>
                  {noPasswordError && (
                    <Alert severity="warning" sx={{ textAlign: "left" }}>
                      <Typography variant="body2" sx={{ mb: 1, fontWeight: 600 }}>
                        Mot de passe non défini
                      </Typography>
                      <Typography variant="body2" sx={{ mb: 1.5 }}>
                        Votre compte existe mais aucun mot de passe n'a été défini. Vérifiez votre boîte email pour le lien de création de mot de passe.
                      </Typography>
                      <Typography variant="body2">
                        Lien expiré ou introuvable ?{" "}
                        <Link to="/forgot-password" style={{ color: "#4BB7A5", fontWeight: 600, textDecoration: "none" }}>
                          Demander un nouveau lien
                        </Link>
                      </Typography>
                    </Alert>
                  )}
                  <TextField
                    label="Email"
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    required
                    fullWidth
                    placeholder="votre.email@exemple.com"
                    autoComplete="email"
                  />

                  <TextField
                    label="Mot de passe"
                    type={showPassword ? "text" : "password"}
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    required
                    fullWidth
                    placeholder="••••••••"
                    autoComplete="current-password"
                    slotProps={{
                      input: {
                        endAdornment: (
                          <InputAdornment position="end">
                            <IconButton onClick={() => setShowPassword(!showPassword)} edge="end" size="small">
                              {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                            </IconButton>
                          </InputAdornment>
                        ),
                      },
                    }}
                  />

                  <Box sx={{ textAlign: "right", mb: 1 }}>
                    <Link
                      to="/forgot-password"
                      style={{ color: "#4BB7A5", textDecoration: "none", fontSize: "0.875rem", fontWeight: 500 }}
                    >
                      Mot de passe oublié ?
                    </Link>
                  </Box>

                  <Button type="submit" variant="contained" size="large" fullWidth disabled={isLoading} sx={{ mt: 2 }}>
                    {isLoading ? (
                      <>
                        <CircularProgress size={24} color="inherit" sx={{ mr: 1 }} />
                        Connexion...
                      </>
                    ) : (
                      "Se connecter"
                    )}
                  </Button>
                </Stack>

                <Box sx={{ mt: 3, textAlign: "center" }}>
                  <Typography variant="body2" color="text.secondary">
                    Vous n'avez pas de compte ?{" "}
                    <Link to="/signup" style={{ color: "#4BB7A5", textDecoration: "none", fontWeight: 600 }}>
                      S'inscrire
                    </Link>
                  </Typography>
                </Box>
              </form>
            )}

            {/* ── Étape 2 : Code 2FA ── */}
            {loginStep === "2FA_REQUIRED" && (
              <form onSubmit={handleVerify2FA}>
                <Stack spacing={3}>
                  <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 1 }}>
                    <IconButton onClick={handleBack} size="small">
                      <ArrowLeft size={18} />
                    </IconButton>
                    <Typography variant="h6" sx={{ fontWeight: 600 }}>
                      Vérification
                    </Typography>
                  </Box>

                  <Typography variant="body2" color="text.secondary" sx={{ textAlign: "center" }}>
                    Un code à 6 chiffres a été envoyé à<br />
                    <strong>{pendingEmailMasked}</strong>
                  </Typography>

                  <TextField
                    inputRef={codeInputRef}
                    label="Code de vérification"
                    value={code}
                    onChange={(e) => handleCodeChange(e.target.value)}
                    required
                    fullWidth
                    placeholder="000000"
                    inputProps={{
                      maxLength: 6,
                      style: { textAlign: "center", fontSize: "1.5rem", letterSpacing: "0.5em" },
                      inputMode: "numeric",
                      pattern: "[0-9]*",
                    }}
                    autoComplete="one-time-code"
                  />

                  <Button
                    type="submit"
                    variant="contained"
                    size="large"
                    fullWidth
                    disabled={isLoading || code.length !== 6}
                  >
                    {isLoading ? (
                      <>
                        <CircularProgress size={24} color="inherit" sx={{ mr: 1 }} />
                        Vérification...
                      </>
                    ) : (
                      "Valider"
                    )}
                  </Button>

                  <Box sx={{ textAlign: "center" }}>
                    <Button
                      variant="text"
                      size="small"
                      onClick={handleResend}
                      disabled={resendCooldown > 0}
                      sx={{ color: "#4BB7A5" }}
                    >
                      {resendCooldown > 0
                        ? `Renvoyer le code (${resendCooldown}s)`
                        : "Renvoyer le code"}
                    </Button>
                  </Box>
                </Stack>
              </form>
            )}

            {/* ── En attente de validation ── */}
            {loginStep === "PENDING_VALIDATION" && (
              <Stack spacing={3} sx={{ textAlign: "center" }}>
                <Typography variant="h6" sx={{ fontWeight: 600, color: "#f59e0b" }}>
                  Compte en attente de validation
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Votre email a été vérifié. Votre compte sera activé après validation par l'équipe Cecodev.
                  Vous recevrez un email de confirmation.
                </Typography>
                <Button variant="outlined" onClick={handleBack}>
                  Retour
                </Button>
              </Stack>
            )}
          </CardContent>
        </Card>
      </Box>
    </Box>
  );
}
